import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { SvgIconComponent } from '@material-ui/icons';
import AddCircle from '@material-ui/icons/AddCircleOutline';
import InsertDriveFile from '@material-ui/icons/InsertDriveFileOutlined';
import React, { useEffect, useState } from 'react';
import { colors } from '../../theme/palette';
import { ViiisionTooltip } from '../ViiisionTooltip';
import { QrIcon } from '../SvgComponents/SvgQr';
import { MultiLingIcon } from '../SvgComponents/SvgMultiLing';
import { ViiisionDrawer } from './ViiisionDrawer';
import MultilanguageDrawer from '../ML/MLDrawer';
import Settings from '@material-ui/icons/Settings';
import { SETTINGS_MENU } from '../../util/constants';
import { focusedItemName } from '../../store/slices/editorSlice';
import { useAppDispatch } from '../../store/hooks';
import { bindActionCreators } from 'redux';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		pageAdd: {
			backgroundColor: colors.grayLight20,
			justifyContent: 'space-between',
		},
		item: {
			'&.Mui-selected': {
				backgroundColor: theme.palette.background.default,
			},
		},
		leftNavItem: {
			paddingTop: 16,
			paddingBottom: 16,
			'&.Mui-selected': {
				backgroundColor: theme.palette.background.default,
				borderRightStyle: 'solid',
				borderRightWidth: '3px',
				borderRightColor: theme.palette.primary.main,
			},
		},
		leftNav: {
			paddingTop: 0,
		},
		iconSelectedColor: {
			color: theme.palette.primary.main,
		},
		iconNotSelectedColor: {
			color: colors.grayDark,
		},
		tourWrapper: {
			height: '100%',
		},
	})
);

export type MenuSelectOption = {
	name: string;
	message: string;
	icon: SvgIconComponent;
};

const defaultOptions: MenuSelectOption[] = [
	{
		name: 'page',
		message: 'Pages',
		icon: InsertDriveFile,
	},
	{
		name: 'element',
		message: 'Elements',
		icon: AddCircle,
	},
	{
		name: 'qr',
		message: 'QR Code',
		icon: QrIcon,
	},
	{
		name: 'multilingual',
		message: 'Multilingual',
		icon: MultiLingIcon,
	},
	{
		name: 'Settings',
		message: 'App Settings',
		icon: Settings,
	},
];

export type MenuSelectProps = {
	selected: string;
	onLeftMenuClick: (menuOption: string) => void;
	options?: MenuSelectOption[];
	editType: 'grapes' | 'tiny' | 'select';
};

const defaultProps: Partial<MenuSelectProps> = {
	options: defaultOptions,
};

export const MenuSelect: React.FC<MenuSelectProps> = (props: MenuSelectProps) => {
	const { selected, onLeftMenuClick, options } = { ...defaultProps, ...props };
	const classes = useStyles();
	const [open, setOpen] = useState(false);
	const [conditionalOptions, setConditionalOptions] = useState(options);
	const dispatch = useAppDispatch();
	const onFocusedItemNameBinder = bindActionCreators(focusedItemName, dispatch);

	const handleSettingsClick = () => {
		onFocusedItemNameBinder(SETTINGS_MENU);
		return false;
	};
	// Set the default options without plan-based filtering
	useEffect(() => {
		setConditionalOptions(options);
		// eslint-disable-next-line
	}, [options]);

	return props.editType !== 'select' ? (
		<ViiisionDrawer anchor="left" isExpanded={false}>
			<MultilanguageDrawer open={open} setOpen={setOpen} />
			<div data-tour="left-tab" className={classes.tourWrapper}>
				<List classes={{ root: classes.leftNav }}>
					{conditionalOptions
						.filter(
							(option) =>
								(props.editType === 'grapes' &&
									option.name !== 'element' &&
									option.name !== 'qrCode') ||
								(props.editType === 'tiny' && option)
						)
						.map((option, index) => (
							<ViiisionTooltip key={option.name} message={option.message} placement="right">
								<ListItem
									key={index}
									button
									onClick={() => {
										if (option.name === 'multilingual') setOpen(true);
										else if (option.name === 'Settings') handleSettingsClick();
										else onLeftMenuClick(option.name);
									}}
									classes={{ root: classes.leftNavItem }}
									selected={selected === option.name}
								>
									<ListItemIcon
										className={
											selected === option.name
												? classes.iconSelectedColor
												: classes.iconNotSelectedColor
										}
									>
										<option.icon />
									</ListItemIcon>
								</ListItem>
							</ViiisionTooltip>
						))}
				</List>
			</div>
		</ViiisionDrawer>
	) : null;
};
